import type { BomEvidence, BomEvidenceMap } from '@/domain/hs1'
import type { PlmAssembly, PlmPart, PlmState } from './contract'

/**
 * Vom PLM-Teil zur Stuecklistenzeile.
 *
 * DIE BRUECKE, DIE BISHER FEHLTE. Das PLM ordnet Teile SEGMENTEN zu — „dieser
 * Traeger gehoert zum Oberschenkel". Das genuegt fuer Masse, Schwerpunkt und
 * Traegheit, also fuer alles, was `rollup.ts` tut. Die Stueckliste fragt aber
 * anders: sie fuehrt POSITIONEN (ein Antriebsmodul, eine Leistungsstufe, ein
 * Akkupack), und ein Segment ist keine Position. Ein Oberschenkel enthaelt ein
 * Antriebsmodul, Struktur und Verkabelung; die Stueckliste fuehrt davon eine
 * Zeile, das PLM zwanzig Teile.
 *
 * Deshalb `PlmPart.bomKey`: das PLM sagt, welche Position ein Teil traegt. Erst
 * damit laesst sich die Frage „welche Zeile kommt aus dem PLM" ueberhaupt
 * beantworten — vorher war sie nicht falsch beantwortet, sondern gar nicht
 * stellbar.
 *
 * HEUTE KOMMT NICHTS ZURUECK. Die Gegenstelle fuellt `bomKey` nicht. Diese
 * Datei liefert dann eine leere Zuordnung, und die Stueckliste sagt an jeder
 * Zeile „geschaetzt" statt „gemessen". Das ist die richtige Auskunft und nicht
 * ein fehlendes Merkmal.
 *
 * DIE RICHTUNG BLEIBT EINE EINBAHNSTRASSE. Hier wird gelesen und uebersetzt.
 * Es gibt keinen Weg zurueck — siehe `PlmClient` in `contract.ts`.
 */

/** Der Reifegrad im Klartext, wie ihn die Oberflaeche zeigt. */
export const PLM_STATE_LABELS: Record<PlmState, string> = {
  entwurf: 'Entwurf',
  in_pruefung: 'in Prüfung',
  freigegeben: 'freigegeben',
}

/**
 * Der schwaechste Reifegrad gewinnt.
 *
 * Eine Position aus fuenf Teilen ist so weit, wie ihr unfertigstes Teil. Den
 * hoechsten zu nehmen waere die bequeme und die falsche Wahl: dann steht
 * „freigegeben" an einer Zeile, in der ein Entwurf steckt.
 */
const RANG: Record<PlmState, number> = { entwurf: 0, in_pruefung: 1, freigegeben: 2 }

const schwaechster = (states: PlmState[]): PlmState =>
  states.reduce((a, b) => (RANG[b] < RANG[a] ? b : a), 'freigegeben')

/**
 * Fasst die Teile je Stuecklistenposition zusammen.
 *
 * Mehrere Teile auf derselben Position werden addiert — ein Antriebsmodul aus
 * Motor, Getriebe und Gehaeuse ist EINE Zeile der Stueckliste und drei Teile
 * im PLM. Die Teilenummer der Zeile ist dann die des schwersten Teils, mit der
 * Zahl der uebrigen daneben: eine erfundene Sammelnummer waere eine Kennung,
 * die es im PLM nicht gibt.
 */
export function bomEvidence(assembly: PlmAssembly | null): BomEvidenceMap {
  if (!assembly) return {}

  const nachSchluessel = new Map<string, PlmPart[]>()
  for (const part of assembly.parts) {
    const key = part.bomKey
    if (!key) continue
    const liste = nachSchluessel.get(key)
    if (liste) liste.push(part)
    else nachSchluessel.set(key, [part])
  }

  const out: Record<string, BomEvidence> = {}
  for (const [key, teile] of nachSchluessel) {
    const schwerstes = teile.reduce((a, b) => (b.mass > a.mass ? b : a))
    const state = schwaechster(teile.map((t) => t.state))
    out[key] = {
      number:
        teile.length > 1
          ? `${schwerstes.number} (+${teile.length - 1})`
          : schwerstes.number,
      revision: schwerstes.revision,
      state: PLM_STATE_LABELS[state],
      mass: teile.reduce((a, t) => a + t.mass, 0),
    }
  }
  return out
}

/**
 * Wie viele Positionen der Stueckliste das PLM ueberhaupt belegen koennte.
 *
 * Fuer die Anzeige: „0 von 24 Positionen belegt" ist eine Auskunft, „keine
 * Daten" ist eine Fehlermeldung. Der Unterschied entscheidet, ob jemand die
 * Anbindung fuer kaputt haelt oder fuer unvollstaendig.
 */
export const evidenceCount = (map: BomEvidenceMap): number => Object.keys(map).length
